import { DateTime } from 'luxon';

import CreativeMediaApproval from '@/common/models/creative/CreativeMediaApproval';
import InventoryTargeting, { IInventoryTargetingAPI } from '@/common/models/InventoryTargeting';
import MediaOwnerApprovalStatus from '@/common/models/media-owner/MediaOwnerApprovalStatus.enum';
import { DASH, fromServerUtcDate } from '@/globals';
import i18n from '@/vueI18n';

export interface IMediaOwnerApprovalAPI {
  creative_mediaowner_approval_id?: number;
  lineitem_mediaowner_approval_id?: number;
  owner_id: number;
  creative_id: number;
  creativefile_id: number;
  lineitem_id: number;
  state: string;
  submitted_at?: string;
  approval_at?: string;
  approval_comment?: string;
  network_id?: number;
  inventorytargeting_id?: number;
  inventory_targeting?: IInventoryTargetingAPI;
}

export default class MediaOwnerApproval {
  id: number;

  mediaOwnerID: number;

  creativeID: number;

  creativefileID: number;

  lineItemID: number;

  state: string;

  submittedAt?: DateTime;

  approvalAt?: DateTime;

  approvalComment?: string;

  networkID?: number;

  ownerID: number;

  inventoryTargetingId?: number;

  inventoryTargeting?: InventoryTargeting;

  constructor(
    id: number,
    mediaOwnerID: number,
    creativeID: number,
    creativefileID: number,
    lineItemID: number,
    state: string,
    submittedAt?: DateTime,
    approvalAt?: DateTime,
    approvalComment?: string,
    networkID?: number,
    inventoryTargetingId?: number,
    inventoryTargeting?: InventoryTargeting,
  ) {
    this.id = id;
    this.mediaOwnerID = mediaOwnerID;
    this.creativeID = creativeID;
    this.creativefileID = creativefileID;
    this.lineItemID = lineItemID;
    this.state = state;
    this.submittedAt = submittedAt;
    this.approvalAt = approvalAt;
    this.approvalComment = approvalComment;
    this.networkID = networkID;
    this.ownerID = mediaOwnerID;
    this.inventoryTargetingId = inventoryTargetingId;
    this.inventoryTargeting = inventoryTargeting;
  }

  get hasComment(): boolean {
    return this.approvalComment !== null && this.approvalComment.trim() !== '';
  }

  get approvalAtFormatted(): string {
    return this.approvalAt !== null ? this.approvalAt.toLocal().toLocaleString(DateTime.DATETIME_SHORT) : DASH;
  }

  get hoursAsDecimal(): number {
    if (this.approvalAt == null) {
      return 0;
    }
    return this.approvalAt.diff(this.approvalAt.startOf('day'), 'hours').hours;
  }

  get hoursAngleInDegrees(): number {
    return ((this.hoursAsDecimal % 12) / 12) * 360;
  }

  get minutesAsDecimal(): number {
    if (this.approvalAt == null) {
      return 0;
    }
    return this.approvalAt.diff(this.approvalAt.startOf('hour'), 'minutes').minutes;
  }

  get minutesAngleInDegrees(): number {
    return (this.minutesAsDecimal / 60) * 360;
  }

  get submittedAtFormatted(): string {
    return this.submittedAt !== null ? this.submittedAt.toLocal().toLocaleString(DateTime.DATETIME_SHORT) : DASH;
  }

  get status(): string {
    switch (this.state) {
      case MediaOwnerApprovalStatus.PENDING_APPROVAL:
        return i18n.global.t('states.pendingApproval');
      case MediaOwnerApprovalStatus.APPROVED:
        return i18n.global.t('states.approved');
      case MediaOwnerApprovalStatus.REJECTED:
        return i18n.global.t('states.rejected');
      default:
        return this.state;
    }
  }

  get statusForLineItemAudit(): string {
    switch (this.state) {
      case MediaOwnerApprovalStatus.PENDING_APPROVAL:
        return i18n.global.t('states.submitted');
      case MediaOwnerApprovalStatus.APPROVED:
        return i18n.global.t('states.approved');
      case MediaOwnerApprovalStatus.REJECTED:
        return i18n.global.t('states.rejected');
      default:
        return this.state;
    }
  }

  static fromApi(apiObject: IMediaOwnerApprovalAPI): MediaOwnerApproval {
    return new MediaOwnerApproval(
      apiObject.creative_mediaowner_approval_id || apiObject.lineitem_mediaowner_approval_id,
      apiObject.owner_id,
      apiObject.creative_id,
      apiObject.creativefile_id,
      apiObject.lineitem_id,
      apiObject.state,
      fromServerUtcDate(apiObject.submitted_at),
      apiObject.approval_at !== null ? fromServerUtcDate(apiObject.approval_at) : null,
      apiObject.approval_comment,
      apiObject.network_id,
      apiObject.inventorytargeting_id,
      apiObject.inventory_targeting ? InventoryTargeting.fromAPI(apiObject.inventory_targeting) : null,
    );
  }

  toApi(): { creative_mediaowner_approval_id: number } {
    return {
      creative_mediaowner_approval_id: this.id,
    };
  }

  toCreativeMediaApproval(): CreativeMediaApproval {
    return new CreativeMediaApproval(
      this.id,
      this.creativeID,
      this.creativefileID,
      null,
      this.approvalAt,
      this.approvalComment,
      this.mediaOwnerID,
      this.state as MediaOwnerApprovalStatus,
      this.submittedAt,
      this.networkID,
      null,
    );
  }
}
